import type { Item } from '@/api/gen/_model/item';
import type { QuizSetData } from '@/api/gen/_model/quizSetData';
import { prepareEquationHTML } from '@/shared/features/rich-text';
import { type QuizType } from '@/types/Quiz';

import { type FlashcardAnswerContent, type FlashcardCard } from '../types';

const resolveMatchItemRichText = (item: Item): string | undefined =>
  item.richText ||
  (item.equation ? prepareEquationHTML(item.equation) : undefined);

const resolveAnswerContent = (
  quiz: QuizSetData,
): FlashcardAnswerContent | null => {
  const quizType = quiz.type as QuizType;

  if (quizType === 'subjective' && quiz.subjectiveData) {
    const values = quiz.subjectiveData.answerList ?? [];
    return { kind: 'text', values };
  }

  if (quizType === 'multiple' && quiz.multipleData) {
    const answerList = quiz.multipleData.answerList ?? [];
    const choices = quiz.multipleData.choiceListV2 ?? [];

    // answerList는 정답 선지의 인덱스 문자열 배열. (예: ['2'] → choices[2])
    const correctChoices = answerList
      .map(indexStr => Number.parseInt(indexStr, 10))
      .filter(
        index =>
          Number.isInteger(index) && index >= 0 && index < choices.length,
      )
      .map(index => choices[index]);

    if (correctChoices.length === 0) {
      return { kind: 'text', values: [] };
    }

    return {
      kind: 'choice',
      items: correctChoices.map(choice => ({
        text: choice.text,
        richText: choice.richText || undefined,
        imageUrl: choice.imageUrl || undefined,
      })),
    };
  }

  if (quizType === 'ox' && quiz.oxData) {
    // API는 소문자 'o' | 'x'로 정답을 저장 (apiAdapter 참고)
    const value = quiz.oxData.answer?.toLowerCase() === 'x' ? 'X' : 'O';
    return { kind: 'ox', value };
  }
  if (quizType === 'essay') {
    return { kind: 'essay' };
  }

  return null;
};

const resolveQuestionText = (quiz: QuizSetData): string => {
  const quizType = quiz.type as QuizType;
  switch (quizType) {
    case 'subjective':
      return quiz.subjectiveData?.question ?? '';
    case 'multiple':
      return quiz.multipleData?.question ?? '';
    case 'ox':
      return quiz.oxData?.question ?? '';
    case 'essay':
      return quiz.essayData?.question ?? '';
    case 'match':
      return quiz.matchData?.question ?? '';
    case 'order':
      return quiz.orderData?.question ?? '';
    default:
      return '';
  }
};

const resolveQuestionRichText = (quiz: QuizSetData): string | undefined => {
  const quizType = quiz.type as QuizType;
  switch (quizType) {
    case 'subjective':
      return quiz.subjectiveData?.questionRichText || undefined;
    case 'multiple':
      return quiz.multipleData?.questionRichText || undefined;
    case 'ox':
      return quiz.oxData?.questionRichText || undefined;
    case 'essay':
      return quiz.essayData?.questionRichText || undefined;
    case 'match':
      return quiz.matchData?.questionRichText || undefined;
    case 'order':
      return quiz.orderData?.questionRichText || undefined;
    default:
      return undefined;
  }
};

/**
 * MATCH 문항을 pair 단위 플래시카드로 확장합니다 (PRD 5.6).
 *
 * - 각 정답 pair = 카드 1장. 4쌍 문항 → 4장.
 * - 앞면: 좌측 항목(텍스트/이미지/수식), 뒷면: 정답 우측 항목.
 * - 문항 지문은 카드 context 로 표시한다.
 * - 카드 id 는 pair 별로 고유해야 세션의 known/review 판정이 카드 단위로 동작한다.
 */
function mapMatchToFlashcards(quiz: QuizSetData): FlashcardCard[] {
  const matchData = quiz.matchData;
  if (!matchData) return [];

  const leftById = new Map(matchData.leftItems.map(item => [item.id, item]));
  const rightById = new Map(matchData.rightItems.map(item => [item.id, item]));
  const context = matchData.questionRichText || matchData.question || undefined;
  const contextContent =
    matchData.question || matchData.questionRichText || quiz.imageUrl
      ? {
          text: matchData.question ?? '',
          richText: matchData.questionRichText || undefined,
          imageUrl: quiz.imageUrl || undefined,
        }
      : undefined;

  return matchData.answerPairs.flatMap(pair => {
    const left = leftById.get(pair.leftId);
    const right = rightById.get(pair.rightId);
    if (!left || !right) return [];

    return [
      {
        id: `${quiz.id}:${pair.leftId}:${pair.rightId}`,
        question: left.text || left.equation || '',
        questionRichText: resolveMatchItemRichText(left),
        questionImageUrl: left.imageUrl || undefined,
        context,
        contextContent,
        answerContent: {
          kind: 'choice',
          items: [
            {
              text: right.text || right.equation || '',
              richText: resolveMatchItemRichText(right),
              imageUrl: right.imageUrl || undefined,
            },
          ],
        },
      } satisfies FlashcardCard,
    ];
  });
}

/**
 * QuizSetData(API 응답) → 플래시카드 카드 배열로 변환합니다.
 *
 * - MATCH 는 정답 pair 마다 1장으로 확장한다.
 * - 그 외 유형은 문항당 1장:
 *   - 앞면: 질문 텍스트 + 선택적 질문 이미지(`imageUrl`)
 *   - 뒷면: 정답만 표시 (객관식의 경우 정답에 해당하는 선지만)
 */
function mapQuizToFlashcards(quiz: QuizSetData): FlashcardCard[] {
  if ((quiz.type as QuizType) === 'match') {
    return mapMatchToFlashcards(quiz);
  }

  const answerContent = resolveAnswerContent(quiz);
  if (!answerContent) return [];

  return [
    {
      id: quiz.id,
      question: resolveQuestionText(quiz),
      questionRichText: resolveQuestionRichText(quiz),
      questionImageUrl: quiz.imageUrl,
      answerContent,
    },
  ];
}

export function mapQuizListToFlashcards(
  quizList: QuizSetData[],
): FlashcardCard[] {
  return quizList.flatMap(mapQuizToFlashcards);
}

export function hasFlashcardCards(quizList: QuizSetData[]): boolean {
  return mapQuizListToFlashcards(quizList).length > 0;
}
